import { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ListChecks, Loader2, Plus, Trash2, ChevronRight } from 'lucide-react'
import { useResource } from '@/shared/hooks/useResources'
import { useBots } from '@/shared/hooks/useBots'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { resourcesApi } from '@/shared/api/resources'
import { botsApi } from '@/shared/api/bots'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import Toast from '@/components/ui/Toast'
import type { Bot } from '@/shared/types'

export default function ResourceWhitelist() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { hapticFeedback, hapticNotification, webApp } = useTelegram()
  const { data: resource, isLoading: resourceLoading } = useResource(resourceId)
  const { data: userBots, isLoading: botsLoading } = useBots(resource?.owner_id)

  // Fetch accessible bots (granted access)
  const { data: accessibleBots, isLoading: accessibleBotsLoading } = useQuery({
    queryKey: ['accessible-bots'],
    queryFn: () => botsApi.listBotsWithAccess(),
  })

  // Combine owned and accessible bots, avoiding duplicates
  const allBots = useMemo(() => {
    const owned = userBots || []
    const accessible = accessibleBots || []
    const ownedIds = new Set(owned.map(b => b.id))
    const combined = [...owned]
    for (const bot of accessible) {
      if (!ownedIds.has(bot.id)) {
        combined.push(bot)
      }
    }
    return combined
  }, [userBots, accessibleBots])

  // Fetch whitelisted bot IDs separately
  const { data: whitelistedBotIds, isLoading: whitelistedIdsLoading } = useQuery({
    queryKey: ['resource-whitelisted-bots', resourceId],
    queryFn: () => resourcesApi.getWhitelistedBots(resourceId!),
    enabled: !!resourceId,
  })

  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [showToast, setShowToast] = useState(false)

  // Auto-manage back button
  useBackButton()

  // Show toast if navigation state contains success message
  useEffect(() => {
    const state = location.state as { successMessage?: string } | null
    if (state?.successMessage) {
      setToastMessage(state.successMessage)
      setShowToast(true)
      setToastType('success')
      // Clear navigation state after a small delay to allow toast to render
      setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} })
      }, 100)
    }
  }, [location.state, navigate, location.pathname])

  // Filter to show only whitelisted bots
  const whitelistedBots = useMemo(() => {
    if (!whitelistedBotIds || !allBots) return []
    return allBots.filter(b => whitelistedBotIds.includes(b.id))
  }, [whitelistedBotIds, allBots])

  // Remove bot mutation
  const removeBotMutation = useMutation({
    mutationFn: async (botId: string) => {
      if (!resourceId || !whitelistedBotIds) throw new Error('Resource or whitelist data is required')
      const updatedList = whitelistedBotIds.filter(id => id !== botId)
      await resourcesApi.setWhitelistedBots(resourceId, updatedList)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resource-whitelisted-bots', resourceId] })
      queryClient.invalidateQueries({ queryKey: ['resource', resourceId] })
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      // Invalidate bot-side queries for bidirectional sync
      queryClient.invalidateQueries({ queryKey: ['bot-whitelisted-resources'] })
      queryClient.invalidateQueries({ queryKey: ['bots'] })
      setToastMessage('Бот успешно удален из списка')
      setToastType('success')
      setShowToast(true)
      hapticNotification('success')
    },
    onError: (error: any) => {
      const message = error.response?.data?.details || error.message || 'Не удалось удалить бота'
      setToastMessage(message)
      setToastType('error')
      setShowToast(true)
      hapticNotification('error')
    },
  })

  const handleRemoveBot = (bot: Bot) => {
    webApp?.showPopup({
      title: 'Удалить бота',
      message: `Вы уверены, что хотите удалить "${bot.name}" из белого списка?`,
      buttons: [
        { id: 'cancel', type: 'cancel' },
        { id: 'confirm', type: 'destructive', text: 'Удалить' },
      ],
    }, (buttonId) => {
      if (buttonId === 'confirm') {
        hapticFeedback('medium')
        removeBotMutation.mutate(bot.id)
      }
    })
  }

  // Helper to get logo URL
  const getLogoUrl = (bot: Bot) => {
    if (!bot.id) return null

    const baseUrl = import.meta.env.VITE_OPSERVICE_BASE_URL || 'http://localhost:8080'
    return `${baseUrl}/api/v1/bots/${bot.id}/logo`
  }

  if (resourceLoading || botsLoading || accessibleBotsLoading || whitelistedIdsLoading || !resource) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
              <ListChecks className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-tg-text">Мои боты</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-tg-text">{whitelistedBots.length}</div>
              <div className="text-xs text-tg-hint">ботов</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Info Card */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
          <p className="text-sm text-blue-400">
            Ваши боты для продвижения этого ресурса. Оплата не требуется.
          </p>
        </div>

        {/* Add Bot Button */}
        <button
          onClick={() => {
            hapticFeedback('light')
            window.scrollTo(0, 0)
            navigate(`/traffic/${resourceId}/whitelist/add`)
          }}
          className="w-full bg-tg-button text-tg-button-text rounded-xl p-4 hover:opacity-90 active:opacity-75 transition-opacity flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Plus className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Добавить бота</div>
              <div className="text-sm opacity-90">Выберите боты для добавления</div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 opacity-75" />
        </button>

        {/* Whitelisted Bots Section */}
        <div className="bg-tg-secondary-bg rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                <ListChecks className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="font-semibold text-tg-text">Добавленные боты ({whitelistedBots.length})</h2>
                <p className="text-xs text-tg-hint">Боты для продвижения ресурса</p>
              </div>
            </div>
          </div>

          {whitelistedBots.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-tg-hint font-medium">Пока нет добавленных ботов</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {whitelistedBots.map((bot) => {
                const logoUrl = getLogoUrl(bot)

                return (
                  <div
                    key={bot.id}
                    onClick={() => {
                      hapticFeedback('light')
                      navigate(`/bots/${bot.id}`, {
                        state: { returnTo: `/traffic/${resourceId}/whitelist` }
                      })
                    }}
                    className="p-4 bg-tg-bg rounded-2xl shadow-sm hover:shadow-md active:scale-[0.98] transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      {/* Bot Logo or Icon */}
                      <div className={`w-12 h-12 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center ${
                        logoUrl ? 'bg-tg-secondary-bg' : 'bg-tg-hint/10'
                      }`}>
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt={bot.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              const parent = target.parentElement
                              if (parent) {
                                parent.innerHTML = `<div class="text-2xl">🤖</div>`
                              }
                            }}
                          />
                        ) : (
                          <div className="text-2xl">🤖</div>
                        )}
                      </div>

                      {/* Bot Info */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-tg-text truncate">
                          {bot.name}
                        </div>
                        <div className="text-sm text-tg-hint truncate">
                          @{bot.username}
                        </div>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemoveBot(bot)
                        }}
                        disabled={removeBotMutation.isPending}
                        className="p-2 text-red-600 hover:bg-red-500/10 rounded-lg transition-colors active:scale-95 disabled:opacity-50 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  )
}
